import { Component, Input, OnInit } from '@angular/core';
import { TranslationService } from '../../../../services/translation.service';
import { MainMenuService } from '../../../../services/main-menu.service';
import { MatMenuModule } from "@angular/material/menu";
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { RtI18nDirective } from '../runtime-translation.directive';
import { Language } from '../../../models/language.model';
/**
 * Language switcher component
 * Provides a dropdown to switch between available languages
 *
 * Usage:
 * <app-language-switcher></app-language-switcher>
 * <app-language-switcher [useSelect]="true"></app-language-switcher>
 */
@Component({
  selector: 'app-language-switcher',
  templateUrl: './language-switcher.component.html',
  styleUrls: ['./language-switcher.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    MatMenuModule, 
    MatFormFieldModule, 
    MatSelectModule,
    RtI18nDirective
  ]
})
export class LanguageSwitcherComponent implements OnInit {
  @Input() hideNonTranslatableLanguages = false;
  @Input() useSelect = false;
  @Input() disabled = false;

  currentLanguage: string;
  currentLanguageId: number | null = null;
  languages: Language[];

  constructor(private translationService: TranslationService,
              private mainMenuService: MainMenuService) {}

  ngOnInit(): void {
    this.translationService.getSupportedLanguages().subscribe(langs => {
      this.languages = langs;

      if (this.hideNonTranslatableLanguages) {
        this.languages = this.languages.filter(lang => lang.IsSupportedInAzure);
      }

      // Load user's language preference from server
      this.translationService.getUserLanguage().subscribe({
        next: async (userLanguage) => {
          if (userLanguage?.AzureLanguageCode) {
            this.currentLanguage = userLanguage.AzureLanguageCode;
            this.currentLanguageId = userLanguage.Id;
            await this.translationService.setLanguage(userLanguage.AzureLanguageCode);
            await this.translationService.setLanguageId(userLanguage.Id);
          }
        },
        error: (error) => {
          console.error('Failed to load user language preference:', error);
          // Fall back to stored language
          this.currentLanguage = this.translationService.getCurrentLanguage();
          this.currentLanguageId = this.translationService.getCurrentLanguageId();
        }
      });
    });

    // Subscribe to language changes
    this.translationService.currentLanguage$.subscribe(lang => {
      this.currentLanguage = lang;
    });
  }

  /**
   * Change the application language
   */
  changeLanguage(lang: string | null): void {
    if (lang) {
      // Find the language object to get the ID
      const selectedLanguage = this.languages.find(l => l.AzureLanguageCode === lang);

      if (selectedLanguage) {
        // Update the language preference on the server
        this.translationService.updateUserLanguage(selectedLanguage.Id).subscribe({
          next: async _ => {
            // Update local language settings
            await this.translationService.setLanguage(lang);
            await this.translationService.setLanguageId(selectedLanguage.Id);
            this.currentLanguageId = selectedLanguage.Id;
            this.currentLanguage = lang;
            // App titles come translated from the server, so pull a fresh menu now
            // that the server knows the new language.
            void this.mainMenuService.refreshMenuItems();
          },
          error: async (error) => {
            console.error('Failed to update user language preference:', error);
            // Still update locally even if server update fails
            await this.translationService.setLanguage(lang);
            await this.translationService.setLanguageId(selectedLanguage.Id);
          }
        });
      }
    }
  }

  /**
   * Handle mat-select change event
   */
  onSelectChange(languageId: number): void {
    const selectedLanguage = this.languages.find(l => l.Id === languageId);

    if (selectedLanguage?.AzureLanguageCode) {
      this.changeLanguage(selectedLanguage.AzureLanguageCode);
    }
  }
}
